/**
 * GroupNode — Container node that holds child nodes rendered at full size by ReactFlow.
 *
 * Unlike designer nodes, GroupNode does NOT use DesignerNodeShell. It has its own UI pattern:
 * a dark container (#141414) with an editable floating label. Children render as their actual
 * ReactFlow node components (e.g., ImageGeneratorNode) at full size inside the container via
 * the `parentNode` mechanism — no scaling, no thumbnail cards.
 */

import React, { memo, useState, useCallback, useMemo, useEffect } from 'react'
import { type NodeProps, useReactFlow, useStore } from 'reactflow'
import { shallow } from 'zustand/shallow'
import { MessageSquarePlus } from 'lucide-react'
import { FloatingNodeLabel } from '../shared'
import { AgentNodeToolbar, type ToolbarAction } from '../shared/AgentNodeToolbar'
import { useNodeHover } from '../../NodeHoverContext'
import { useCanvasOptional } from '../../CanvasContext'
import { useNodeActions } from '../shared/useNodeActions'
import { getGroupCategory, getDefaultChildType, getMediaCategory, type MediaCategory } from './groupValidation'
import { getNodeDimensions } from '../../nodeRegistry'
import { useGroupDropTarget } from './GroupDropTargetContext'
import { AddPanelButton, AddPlaceholderSlot } from './AddPanelButton'
import { GroupDeleteDialog } from './GroupDeleteDialog'
import { ReferenceSelector, type SelectedReference } from '../shared/ReferenceSelector'
import { removeNodesFromGraph } from '../../../../api/client'
import { useProjectId } from '../../../../contexts/ProjectIdContext'

export type LayoutMode = 'horizontal' | 'vertical' | 'auto'

/**
 * Returns the row and column indices for a child at the given index,
 * based on the layout mode. Used to filter between-item add buttons
 * so they only appear between items in the same row (horizontal/auto)
 * or same column (vertical).
 */
export function getRowCol(index: number, layoutMode: LayoutMode): { row: number; col: number } {
  switch (layoutMode) {
    case 'horizontal':
      return { row: 0, col: index }
    case 'vertical':
      return { row: index, col: 0 }
    case 'auto':
    default:
      return { row: Math.floor(index / COLUMNS), col: index % COLUMNS }
  }
}

export interface GroupNodeData {
  in_label?: string
  label?: string
  in_child_node_ids?: (string | null)[]
  in_layout_mode?: LayoutMode
}

// Child layout constants
export const CHILD_WIDTH = 315     // default ImageGeneratorNode width (16:9)
export const CHILD_HEIGHT = 300    // estimated child height
export const CHILD_GAP = 24
export const COLUMNS = 3
export const CONTAINER_PADDING = 32
/** Height of the FloatingNodeLabel above each child node (text-sm ~20px + marginBottom 6px) */
export const CHILD_LABEL_HEIGHT = 26

/**
 * Compute the cell size for a group's children by finding the maximum
 * width and full height across all child nodes. Full height includes
 * the image/content area (layout.height), the floating label above
 * (CHILD_LABEL_HEIGHT), and any per-type footer (footerHeight from schema).
 * Falls back to CHILD_WIDTH/CHILD_HEIGHT when the group is empty or
 * children have no schema.
 */
export function computeCellSize(
  childNodeIds: (string | null)[],
  allNodes: Array<{ id: string; type?: string; data?: unknown }>,
): { cellWidth: number; cellHeight: number } {
  const actualIds = childNodeIds.filter((id): id is string => id !== null)
  if (actualIds.length === 0) {
    return { cellWidth: CHILD_WIDTH, cellHeight: CHILD_HEIGHT }
  }

  let maxWidth = 0
  let maxHeight = 0

  for (const childId of actualIds) {
    const node = allNodes.find((n) => n.id === childId)
    if (!node?.type) continue
    const nodeData = node.data as Record<string, unknown> | undefined
    const aspectRatio = (nodeData?.aspectRatio as string) || undefined
    const dims = getNodeDimensions(node.type, aspectRatio)
    if (dims.width > maxWidth) maxWidth = dims.width
    const fullHeight = dims.height + CHILD_LABEL_HEIGHT + dims.footerHeight
    if (fullHeight > maxHeight) maxHeight = fullHeight
  }

  if (maxWidth === 0 || maxHeight === 0) {
    return { cellWidth: CHILD_WIDTH, cellHeight: CHILD_HEIGHT }
  }

  return { cellWidth: maxWidth, cellHeight: maxHeight }
}

/**
 * Compute the position {x, y} for a child at the given index
 * based on the active layout mode.
 */
export function computeChildPosition(
  index: number,
  layoutMode: LayoutMode,
  cellWidth: number = CHILD_WIDTH,
  cellHeight: number = CHILD_HEIGHT,
): { x: number; y: number } {
  let col: number
  let row: number

  switch (layoutMode) {
    case 'horizontal':
      col = index
      row = 0
      break
    case 'vertical':
      col = 0
      row = index
      break
    case 'auto':
    default:
      col = index % COLUMNS
      row = Math.floor(index / COLUMNS)
      break
  }

  return {
    x: CONTAINER_PADDING + col * (cellWidth + CHILD_GAP),
    y: CONTAINER_PADDING + row * (cellHeight + CHILD_GAP),
  }
}

/**
 * Compute the container {width, height} for the given child count
 * and layout mode.
 */
export function computeContainerSize(
  childCount: number,
  layoutMode: LayoutMode,
  cellWidth: number = CHILD_WIDTH,
  cellHeight: number = CHILD_HEIGHT,
): { width: number; height: number } {
  const count = childCount || 1 // minimum 1 for empty state

  let cols: number
  let rows: number

  switch (layoutMode) {
    case 'horizontal':
      cols = count
      rows = 1
      break
    case 'vertical':
      cols = 1
      rows = count
      break
    case 'auto':
    default:
      cols = Math.min(count, COLUMNS)
      rows = Math.max(Math.ceil(count / COLUMNS), 1)
      break
  }

  return {
    width: 2 * CONTAINER_PADDING + cols * cellWidth + (cols - 1) * CHILD_GAP,
    height: 2 * CONTAINER_PADDING + rows * cellHeight + (rows - 1) * CHILD_GAP,
  }
}

/**
 * Compute the new in_child_node_ids array after removing a child.
 * Always replaces the removed child with null (preserving the gap).
 * Trailing nulls are trimmed.
 */
export function removeChildFromIds(
  childId: string,
  childNodeIds: (string | null)[],
): (string | null)[] {
  const newIds = childNodeIds.map((id) => (id === childId ? null : id))

  // Trim trailing nulls
  while (newIds.length > 0 && newIds[newIds.length - 1] === null) {
    newIds.pop()
  }

  return newIds
}

const GroupNode = memo(function GroupNode(props: NodeProps<GroupNodeData>) {
  const { id, data, selected, dragging } = props
  const { setNodes, setEdges, getNode, getNodes } = useReactFlow()
  // Render-path selector: only re-renders when node id/type/data/position structurally changes.
  // Avoids O(n) re-renders during drag that useNodes() causes.
  const allNodes = useStore(
    useCallback(
      (state: { getNodes: () => Array<{ id: string; type?: string; data?: unknown; position: { x: number; y: number }; parentNode?: string; width?: number | null; height?: number | null }> }) =>
        state.getNodes().map(n => ({ id: n.id, type: n.type, data: n.data, position: n.position, parentNode: n.parentNode, width: n.width, height: n.height })),
      [],
    ),
    shallow,
  )
  const canvas = useCanvasOptional()
  const projectId = useProjectId()
  const { handleAddToChat } = useNodeActions({ nodeId: id })
  const { setHoveredNodeId } = useNodeHover()
  const [isHovered, setIsHovered] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showRefSelector, setShowRefSelector] = useState(false)
  const [refSelectorInsertIndex, setRefSelectorInsertIndex] = useState(0)

  // Drop target state from canvas drag handler
  const dropTargetCtx = useGroupDropTarget()
  const isDropTarget = dropTargetCtx?.dropTarget.targetGroupId === id
  const dropRejection = isDropTarget ? dropTargetCtx?.dropTarget.rejection : null
  const insertionIndex = isDropTarget ? (dropTargetCtx?.dropTarget.insertionIndex ?? 0) : -1
  const dropAction = isDropTarget ? dropTargetCtx?.dropTarget.dropAction : null

  // Drag-out visual state
  const dragOutState = dropTargetCtx?.dropTarget.dragOutState
  const isDragOutTarget = dragOutState?.sourceGroupId === id
  const dragOutFactor = isDragOutTarget ? dragOutState!.distanceFactor : 0

  const effectiveDragId = dropTargetCtx?.activeDragNodeId || null

  // "Removed..." flash lifecycle: pick up from context, drive local fade-out timer
  const [localRemovedFlash, setLocalRemovedFlash] = useState<{
    nodeName: string
    groupName: string
  } | null>(null)

  const removedFlash = dropTargetCtx?.dropTarget.removedFlash
  useEffect(() => {
    if (removedFlash && removedFlash.sourceGroupId === id) {
      setLocalRemovedFlash({ nodeName: removedFlash.nodeName, groupName: removedFlash.groupName })
      dropTargetCtx?.setRemovedFlash(null)
      const timer = setTimeout(() => setLocalRemovedFlash(null), 1500)
      return () => clearTimeout(timer)
    }
  }, [removedFlash, id, dropTargetCtx])

  // Resolve child nodes
  const childNodeIds = useMemo(() => data.in_child_node_ids || [], [data.in_child_node_ids])
  const actualChildIds = useMemo(() => childNodeIds.filter((cid): cid is string => cid !== null), [childNodeIds])

  // Resolve display name
  const groupName = data.in_label || data.label || 'Group'

  // Add child node at a given index
  const handleAddPanel = useCallback(
    (insertIndex: number, childTypeOverride?: string) => {
      if (!canvas) return

      const currentNodes = getNodes()
      // Compute position for the new child
      const layoutMode: LayoutMode = data.in_layout_mode || 'auto'
      const { cellWidth, cellHeight } = computeCellSize(
        (data.in_child_node_ids || []).filter((cid): cid is string => cid !== null),
        currentNodes,
      )
      const { x, y } = computeChildPosition(insertIndex, layoutMode, cellWidth, cellHeight)

      // 1. Determine child type: use override if provided, otherwise infer from group
      let childType: string
      if (childTypeOverride) {
        childType = childTypeOverride
      } else {
        const groupCategory = getGroupCategory(
          (data.in_child_node_ids || []).filter((cid): cid is string => cid !== null),
          (nodeId) => currentNodes.find((n) => n.id === nodeId)?.type,
        )
        childType = getDefaultChildType(groupCategory)
      }
      const newNodeId = canvas.addNode(childType, { x, y }, {})

      // 2. Insert the new node ID into in_child_node_ids at the clicked position
      const currentIds = data.in_child_node_ids || []
      const newIds = [...currentIds]
      newIds.splice(insertIndex, 0, newNodeId)

      // 3. Single setNodes call: set parentNode on new child, reposition
      //    all children, and update group's in_child_node_ids atomically
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === newNodeId) return { ...n, parentNode: id, position: { x, y } }
          // Reposition existing children to their new grid positions
          const childIdx = newIds.indexOf(n.id)
          if (childIdx !== -1 && n.id !== newNodeId) {
            const pos = computeChildPosition(childIdx, layoutMode, cellWidth, cellHeight)
            return { ...n, position: pos }
          }
          if (n.id === id) return { ...n, data: { ...n.data, in_child_node_ids: newIds } }
          return n
        })
      )

      // 4. Persist parentNode and in_child_node_ids to Y doc
      canvas.updateNodeParent(newNodeId, id)
      canvas.updateNodeData(id, { in_child_node_ids: newIds })
    },
    [canvas, id, data.in_child_node_ids, data.in_layout_mode, setNodes, getNodes],
  )

  // Rename handler — local update + Y doc persistence
  const handleRename = useCallback(
    (newName: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_label: newName } } : n,
        ),
      )
      canvas?.updateNodeData(id, { in_label: newName })
    },
    [id, setNodes, canvas],
  )

  // Layout mode change handler — local update + Y doc persistence + reposition children
  const handleLayoutModeChange = useCallback(
    (newMode: LayoutMode) => {
      const currentNodes = getNodes()
      const currentIds = data.in_child_node_ids || []
      const { cellWidth: cw, cellHeight: ch } = computeCellSize(
        currentIds.filter((cid): cid is string => cid !== null),
        currentNodes,
      )
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === id) {
            return { ...n, data: { ...n.data, in_layout_mode: newMode } }
          }
          const childIdx = currentIds.indexOf(n.id)
          if (childIdx !== -1) {
            const pos = computeChildPosition(childIdx, newMode, cw, ch)
            return { ...n, position: pos }
          }
          return n
        }),
      )
      canvas?.updateNodeData(id, { in_layout_mode: newMode })
    },
    [id, data.in_child_node_ids, setNodes, canvas, getNodes],
  )

  // One-time organize handler — compacts all gaps
  const handleOrganize = useCallback(() => {
    const currentIds = data.in_child_node_ids || []
    const hasGaps = currentIds.some((cid) => cid === null)
    if (!hasGaps) return

    const currentNodes = getNodes()
    const compactedIds = currentIds.filter((cid): cid is string => cid !== null)
    const layoutModeVal: LayoutMode = data.in_layout_mode || 'auto'
    const { cellWidth, cellHeight } = computeCellSize(compactedIds, currentNodes)

    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === id) {
          return {
            ...n,
            data: { ...n.data, in_child_node_ids: compactedIds },
          }
        }
        const childIdx = compactedIds.indexOf(n.id)
        if (childIdx !== -1) {
          const pos = computeChildPosition(childIdx, layoutModeVal, cellWidth, cellHeight)
          return { ...n, position: pos }
        }
        return n
      }),
    )

    canvas?.updateNodeData(id, { in_child_node_ids: compactedIds })
  }, [id, data.in_child_node_ids, data.in_layout_mode, setNodes, canvas, getNodes])


  // Shared click handler for all empty slots — opens ReferenceSelector
  const handleEmptySlotClick = useCallback((insertIndex: number) => {
    setRefSelectorInsertIndex(insertIndex)
    setShowRefSelector(true)
  }, [])

  // Remove an empty (null) slot from in_child_node_ids
  const handleRemoveEmptySlot = useCallback(
    (slotIndex: number) => {
      if (!canvas) return

      const currentIds = [...(data.in_child_node_ids || [])]
      if (slotIndex < 0 || slotIndex >= currentIds.length || currentIds[slotIndex] !== null) return

      // Splice out the null at this index
      currentIds.splice(slotIndex, 1)

      // Trim trailing nulls
      while (currentIds.length > 0 && currentIds[currentIds.length - 1] === null) {
        currentIds.pop()
      }

      // Recompute layout and reposition children
      const currentNodes = getNodes()
      const layoutModeVal: LayoutMode = data.in_layout_mode || 'auto'
      const nonNullIds = currentIds.filter((cid): cid is string => cid !== null)
      const { cellWidth: cw, cellHeight: ch } = computeCellSize(nonNullIds, currentNodes)

      setNodes((nds) =>
        nds.map((n) => {
          if (n.id === id) {
            return { ...n, data: { ...n.data, in_child_node_ids: currentIds } }
          }
          const childIdx = currentIds.indexOf(n.id)
          if (childIdx !== -1) {
            const pos = computeChildPosition(childIdx, layoutModeVal, cw, ch)
            return { ...n, position: pos }
          }
          return n
        }),
      )

      canvas.updateNodeData(id, { in_child_node_ids: currentIds })
    },
    [canvas, id, data.in_child_node_ids, data.in_layout_mode, setNodes, getNodes],
  )

  // Determine visible tabs for ReferenceSelector based on group category
  const groupCategory: MediaCategory | null = useMemo(() => {
    return getGroupCategory(
      actualChildIds,
      (nodeId) => allNodes.find((n) => n.id === nodeId)?.type,
    )
  }, [actualChildIds, allNodes])

  const refSelectorVisibleTabs = useMemo((): ('image' | 'video')[] => {
    if (groupCategory === 'image') return ['image']
    if (groupCategory === 'video') return ['video']
    return ['image', 'video']
  }, [groupCategory])

  // onSave handler for ReferenceSelector — absorbs selected nodes into the group
  const handleRefSelectorSave = useCallback((references: SelectedReference[]) => {
    setShowRefSelector(false)
    if (!canvas || references.length === 0) return

    const currentNodes = getNodes()
    const layoutModeVal: LayoutMode = data.in_layout_mode || 'auto'
    const currentIds = [...(data.in_child_node_ids || [])]

    // Absorb each selected reference into the group
    let insertIdx = refSelectorInsertIndex
    for (const ref of references) {
      // Check if the node is a standalone canvas node (no parentNode, not a group)
      const refNode = currentNodes.find((n) => n.id === ref.nodeId)
      if (!refNode) continue
      if (refNode.parentNode) continue // already in a group
      if (refNode.type === 'nodeGroup') continue // can't nest groups

      // Check category compatibility
      const nodeCategory = getMediaCategory(ref.nodeType)
      if (nodeCategory === null) continue // unsupported type
      const currentGroupCategory = getGroupCategory(
        currentIds.filter((cid): cid is string => cid !== null),
        (nodeId) => currentNodes.find((n) => n.id === nodeId)?.type,
      )
      if (currentGroupCategory !== null && nodeCategory !== currentGroupCategory) continue // category mismatch

      // Insert at the target index (replace null if available, otherwise splice)
      if (insertIdx < currentIds.length && currentIds[insertIdx] === null) {
        currentIds[insertIdx] = ref.nodeId
      } else {
        currentIds.splice(insertIdx, 0, ref.nodeId)
      }
      insertIdx++
    }

    // Recompute cell size and reposition all children
    const nonNullIds = currentIds.filter((cid): cid is string => cid !== null)
    const { cellWidth: cw, cellHeight: ch } = computeCellSize(nonNullIds, currentNodes)

    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === id) {
          return { ...n, data: { ...n.data, in_child_node_ids: currentIds } }
        }
        const childIdx = currentIds.indexOf(n.id)
        if (childIdx !== -1) {
          const pos = computeChildPosition(childIdx, layoutModeVal, cw, ch)
          return { ...n, parentNode: id, position: pos, extent: undefined }
        }
        return n
      }),
    )

    canvas.updateNodeData(id, { in_child_node_ids: currentIds })

    // Persist parentNode for each newly absorbed child
    for (const ref of references) {
      if (currentIds.includes(ref.nodeId)) {
        canvas.updateNodeParent(ref.nodeId, id)
      }
    }
  }, [canvas, id, data.in_child_node_ids, data.in_layout_mode, refSelectorInsertIndex, setNodes, getNodes])

  // Container sizing based on child count and layout mode
  const layoutMode: LayoutMode = data.in_layout_mode || 'auto'

  // Toolbar actions — layout mode buttons + rearrange action
  const hasGaps = childNodeIds.some((cid) => cid === null)
  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'layout-horizontal',
        label: 'Horizontal',
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16" />
          </svg>
        ),
        onClick: () => handleLayoutModeChange('horizontal'),
        variant: layoutMode === 'horizontal' ? 'primary' : 'default',
      },
      {
        key: 'layout-vertical',
        label: 'Vertical',
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16M6 4v16" />
          </svg>
        ),
        onClick: () => handleLayoutModeChange('vertical'),
        variant: layoutMode === 'vertical' ? 'primary' : 'default',
      },
      {
        key: 'layout-auto',
        label: 'Auto Grid',
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5h4v4H4V5zm6 0h4v4h-4V5zm6 0h4v4h-4V5zM4 11h4v4H4v-4zm6 0h4v4h-4v-4z" />
          </svg>
        ),
        onClick: () => handleLayoutModeChange('auto'),
        variant: layoutMode === 'auto' ? 'primary' : 'default',
      },
      {
        key: 'addToChat',
        label: 'Add to Chat',
        icon: <MessageSquarePlus size={14} />,
        onClick: handleAddToChat,
        group: true,
      },
      {
        key: 'organize',
        label: 'Organize',
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        ),
        onClick: handleOrganize,
        variant: 'default',
        group: true,
        disabled: !hasGaps,
      },
    ],
    [layoutMode, hasGaps, handleLayoutModeChange, handleOrganize, handleAddToChat],
  )

  // Hover handlers
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
    setHoveredNodeId(id)
  }, [id, setHoveredNodeId])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
    setHoveredNodeId(null)
  }, [setHoveredNodeId])

  // Intercept Delete/Backspace when group is selected — show confirmation dialog
  useEffect(() => {
    if (!selected) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return

      // Skip when user is editing text (e.g., FloatingNodeLabel input)
      const active = document.activeElement
      if (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active?.getAttribute('contenteditable') === 'true'
      ) {
        return
      }

      // Prevent ReactFlow's default delete behavior
      e.stopPropagation()
      e.preventDefault()
      setShowDeleteDialog(true)
    }

    // Use capture phase to intercept before ReactFlow's handler
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [selected])

  const handleKeepChildren = useCallback(() => {
    setShowDeleteDialog(false)

    // Get group's absolute position
    const groupNode = getNode(id)
    if (!groupNode) return

    const groupPos = groupNode.position

    // Update children: remove parentNode, convert to absolute positions
    setNodes((nds) =>
      nds.map((n) => {
        if (childNodeIds.includes(n.id)) {
          return {
            ...n,
            parentNode: undefined,
            position: {
              x: groupPos.x + n.position.x,
              y: groupPos.y + n.position.y,
            },
          }
        }
        return n
      })
    )

    // Remove the group node (now empty) and its edges
    canvas?.removeNode(id)
  }, [id, childNodeIds, getNode, setNodes, canvas])

  const handleDeleteAll = useCallback(() => {
    setShowDeleteDialog(false)

    // Collect all node IDs to remove: children + group itself
    const idsToRemove = new Set<string>()
    for (const childId of childNodeIds) {
      if (childId !== null) idsToRemove.add(childId)
    }
    idsToRemove.add(id)

    // Atomic removal: single setNodes call removes all nodes at once,
    // preventing intermediate states where children reference a deleted parent
    // (which causes ReactFlow's createNodeInternals to crash with "Parent node not found")
    setNodes((nds) => nds.filter((n) => !idsToRemove.has(n.id)))
    setEdges((eds) =>
      eds.filter((e) => !idsToRemove.has(e.source) && !idsToRemove.has(e.target)),
    )

    // Sync removal to backend in a single batch call
    if (projectId) {
      removeNodesFromGraph(projectId, Array.from(idsToRemove)).catch((error) => {
        console.error('[GroupNode:handleDeleteAll] Backend removal failed:', error)
      })
    }
  }, [id, childNodeIds, projectId, setNodes, setEdges])

  // Border states per spec:
  // - Default: 1px solid rgba(255,255,255,0.15)
  // - Hover:   1px solid rgba(255,255,255,0.4)
  // - Selected: 2px solid #0074FE
  // Use padding compensation to prevent 1px layout shift between 1px and 2px borders.
  const isSelected = !!selected
  const borderStyle: React.CSSProperties = (() => {
    // Drag-out red gradient (highest priority during active drag-out)
    if (isDragOutTarget && dragOutFactor > 0) {
      return {
        border: `2px solid rgba(255, 61, 0, ${dragOutFactor})`,
        padding: CONTAINER_PADDING,
      }
    }
    // Drop target states (highest priority after selection)
    if (isDropTarget && dropRejection === null) {
      return { border: '2px solid #00C853', padding: CONTAINER_PADDING }
    }
    if (isDropTarget && dropRejection === 'category_mismatch') {
      return { border: '2px solid #FF3D00', padding: CONTAINER_PADDING }
    }
    // 'unsupported' rejection: no highlight change — fall through to normal states
    if (isSelected) {
      return { border: '2px solid #0074FE', padding: CONTAINER_PADDING }
    }
    if (isHovered) {
      return { border: '1px solid rgba(255,255,255,0.4)', padding: CONTAINER_PADDING + 1 }
    }
    return { border: '1px solid rgba(255,255,255,0.15)', padding: CONTAINER_PADDING + 1 }
  })()

  // Container sizing based on child count
  const { cellWidth, cellHeight } = computeCellSize(actualChildIds, allNodes)
  // Size for actual children only (no end-of-grid placeholder)
  const slotCountForSizing = childNodeIds.length || 1 // minimum 1 for empty state
  const { width: containerWidth, height: containerHeight } = computeContainerSize(
    slotCountForSizing,
    layoutMode,
    cellWidth,
    cellHeight,
  )

  return (
    <div className="relative w-fit" data-testid="group-node">
      {/* Inject fadeOut keyframes for removed flash */}
      <style>{`
        @keyframes groupNodeFadeOut {
          0% { opacity: 1; }
          70% { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
      <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

      {/* Floating label — top-left of container (spec §Naming reqs 6-8) */}
      <FloatingNodeLabel
        name={groupName}
        fallback="Group"
        onRename={handleRename}
      />

      {/* Drag-out overlay: "Removing {nodeName} from {groupName}"
          Positioned above the container (outside it) so it never requires z-index elevation. */}
      {isDragOutTarget && dragOutFactor > 0 && (
        <div
          data-testid="drag-out-overlay"
          style={{
            position: 'absolute',
            top: -8,
            right: 0,
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              color: `rgba(255, 61, 0, ${dragOutFactor})`,
              fontSize: 12,
              fontWeight: 600,
              userSelect: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Removing {dragOutState!.nodeName} from {dragOutState!.groupName}
          </span>
        </div>
      )}

      {/* Removed flash: "Removed {nodeName} from {groupName}"
          Positioned above the container (outside it) so it never requires z-index elevation. */}
      {localRemovedFlash && (
        <div
          data-testid="removed-flash"
          style={{
            position: 'absolute',
            top: -8,
            right: 0,
            pointerEvents: 'none',
            animation: 'groupNodeFadeOut 1.5s ease-out forwards',
          }}
        >
          <span
            style={{
              color: '#00C853',
              fontSize: 12,
              fontWeight: 600,
              userSelect: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Removed {localRemovedFlash.nodeName} from {localRemovedFlash.groupName}
          </span>
        </div>
      )}

      {/* Main container — children are rendered by ReactFlow via parentNode */}
      <div
        style={{
          background: '#141414',
          borderRadius: 12,
          width: containerWidth,
          minHeight: containerHeight,
          transition: 'border-color 0.15s ease',
          position: 'relative',
          ...borderStyle,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {actualChildIds.length === 0 && (
          <div
            style={{
              position: 'absolute',
              left: CONTAINER_PADDING,
              top: CONTAINER_PADDING,
              zIndex: 10,
            }}
          >
            <AddPlaceholderSlot
              width={cellWidth}
              height={cellHeight}
              insertIndex={0}
              onClick={handleEmptySlotClick}
            />
          </div>
        )}

        {/* n+1 add buttons — visible on hover, positioned at midpoints/edges */}
        {isHovered && actualChildIds.length > 0 && (() => {
          // Build descriptors for n+1 buttons (before first, between each pair, after last)
          const buttonDescriptors: { insertIndex: number; x: number; y: number; neighborType?: string }[] = []

          // Get positions and indices of actual (non-null) children in the childNodeIds array
          const actualEntries = childNodeIds
            .map((cid, idx) => ({ cid, idx }))
            .filter((e): e is { cid: string; idx: number } => e.cid !== null)

          // Determine the neighbor type for the "add" action from the first child
          const firstChildNode = allNodes.find((n) => n.id === actualEntries[0]?.cid)
          const defaultNeighborType = firstChildNode?.type || undefined

          // Button before first child
          if (actualEntries.length > 0) {
            const firstPos = computeChildPosition(actualEntries[0].idx, layoutMode, cellWidth, cellHeight)
            if (layoutMode === 'vertical') {
              // Above first child, horizontally centered
              buttonDescriptors.push({
                insertIndex: 0,
                x: firstPos.x + cellWidth / 2,
                y: firstPos.y - CHILD_GAP / 2,
                neighborType: defaultNeighborType,
              })
            } else {
              // Left of first child, vertically centered
              buttonDescriptors.push({
                insertIndex: 0,
                x: firstPos.x - CHILD_GAP / 2,
                y: firstPos.y + cellHeight / 2,
                neighborType: defaultNeighborType,
              })
            }
          }

          // Buttons between consecutive actual children
          for (let ai = 0; ai < actualEntries.length - 1; ai++) {
            const curr = actualEntries[ai]
            const next = actualEntries[ai + 1]
            const pos1 = computeChildPosition(curr.idx, layoutMode, cellWidth, cellHeight)
            const pos2 = computeChildPosition(next.idx, layoutMode, cellWidth, cellHeight)
            const rc1 = getRowCol(curr.idx, layoutMode)
            const rc2 = getRowCol(next.idx, layoutMode)

            const precedingNode = allNodes.find((n) => n.id === curr.cid)
            const neighborType = precedingNode?.type || undefined

            if (layoutMode === 'vertical') {
              // Vertical: midpoint between stacked children
              buttonDescriptors.push({
                insertIndex: next.idx,
                x: (pos1.x + cellWidth / 2 + pos2.x + cellWidth / 2) / 2,
                y: (pos1.y + cellHeight + pos2.y) / 2,
                neighborType,
              })
            } else if (rc1.row === rc2.row) {
              // Same row: midpoint between adjacent horizontal children
              buttonDescriptors.push({
                insertIndex: next.idx,
                x: (pos1.x + cellWidth + pos2.x) / 2,
                y: (pos1.y + cellHeight / 2 + pos2.y + cellHeight / 2) / 2,
                neighborType,
              })
            } else {
              // Row break: button to the right of the last child in the current row
              buttonDescriptors.push({
                insertIndex: next.idx,
                x: pos1.x + cellWidth + CHILD_GAP / 2,
                y: pos1.y + cellHeight / 2,
                neighborType,
              })
            }
          }

          // Button after last child
          if (actualEntries.length > 0) {
            const lastEntry = actualEntries[actualEntries.length - 1]
            const lastPos = computeChildPosition(lastEntry.idx, layoutMode, cellWidth, cellHeight)
            const lastChildNode = allNodes.find((n) => n.id === lastEntry.cid)
            const lastNeighborType = lastChildNode?.type || undefined

            if (layoutMode === 'vertical') {
              // Below last child, horizontally centered
              buttonDescriptors.push({
                insertIndex: childNodeIds.length,
                x: lastPos.x + cellWidth / 2,
                y: lastPos.y + cellHeight + CHILD_GAP / 2,
                neighborType: lastNeighborType,
              })
            } else {
              // Right of last child, vertically centered
              buttonDescriptors.push({
                insertIndex: childNodeIds.length,
                x: lastPos.x + cellWidth + CHILD_GAP / 2,
                y: lastPos.y + cellHeight / 2,
                neighborType: lastNeighborType,
              })
            }
          }

          return buttonDescriptors.map((desc, bi) => (
            <div
              key={`add-btn-${bi}`}
              style={{
                position: 'absolute',
                left: desc.x - 9,
                top: desc.y - 9,
                zIndex: 10,
              }}
            >
              <AddPanelButton
                insertIndex={desc.insertIndex}
                onClick={(idx) => handleAddPanel(idx, desc.neighborType)}
              />
            </div>
          ))
        })()}

        {/* Gap-fill empty slots — always visible when gaps exist */}
        {childNodeIds.some((cid) => cid === null) && (
          <>
            {childNodeIds.map((childId, i) => {
              if (childId !== null) return null
              const pos = computeChildPosition(i, layoutMode, cellWidth, cellHeight)

              return (
                <div
                  key={`gap-${i}`}
                  style={{
                    position: 'absolute',
                    left: pos.x,
                    top: pos.y,
                    zIndex: 10,
                  }}
                >
                  <AddPlaceholderSlot
                    width={cellWidth}
                    height={cellHeight}
                    insertIndex={i}
                    onClick={handleEmptySlotClick}
                    onRemove={() => handleRemoveEmptySlot(i)}
                  />
                </div>
              )
            })}
          </>
        )}

        {/* Insertion indicator — shows where the dragged node will land */}
        {isDropTarget && dropRejection === null && dropAction !== 'replace' && (() => {
          const indicatorPos = computeChildPosition(insertionIndex, layoutMode, cellWidth, cellHeight)
          const isVerticalLayout = layoutMode === 'vertical'
          return (
            <div
              data-testid="insertion-indicator"
              style={{
                position: 'absolute',
                left: isVerticalLayout
                  ? indicatorPos.x
                  : indicatorPos.x - 2,
                top: isVerticalLayout
                  ? indicatorPos.y - 2
                  : indicatorPos.y,
                width: isVerticalLayout ? cellWidth : 4,
                height: isVerticalLayout ? 4 : cellHeight,
                background: '#00C853',
                borderRadius: 2,
                pointerEvents: 'none',
                zIndex: 10,
                transition: 'left 0.15s ease, top 0.15s ease',
              }}
            />
          )
        })()}

        {/* Replace indicator — highlights the empty slot being replaced */}
        {isDropTarget && dropRejection === null && dropAction === 'replace' && (() => {
          const replacePos = computeChildPosition(insertionIndex, layoutMode, cellWidth, cellHeight)
          return (
            <div
              data-testid="replace-indicator"
              style={{
                position: 'absolute',
                left: replacePos.x,
                top: replacePos.y,
                width: cellWidth,
                height: cellHeight,
                border: '2px solid #00C853',
                borderRadius: 8,
                background: 'rgba(0, 200, 83, 0.08)',
                pointerEvents: 'none',
                zIndex: 10,
                transition: 'left 0.15s ease, top 0.15s ease',
              }}
            />
          )
        })()}

        {/* Ghost placeholder — shows at the vacated slot during cross-group drag only.
            Hidden during within-group drag because the live reorder preview fills the gap. */}
        {effectiveDragId && childNodeIds.includes(effectiveDragId) && !actualChildIds.includes(effectiveDragId) && (() => {
          const ghostIndex = childNodeIds.indexOf(effectiveDragId)
          if (ghostIndex === -1) return null
          const ghostPos = computeChildPosition(ghostIndex, layoutMode, cellWidth, cellHeight)
          return (
            <div
              data-testid="ghost-placeholder"
              style={{
                position: 'absolute',
                left: ghostPos.x,
                top: ghostPos.y,
                width: cellWidth,
                height: cellHeight,
                background: 'rgba(255,255,255,0.03)',
                border: '2px dashed rgba(255,255,255,0.15)',
                borderRadius: 8,
                pointerEvents: 'none',
                zIndex: 1,
              }}
            />
          )
        })()}

      </div>

      <GroupDeleteDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onKeepChildren={handleKeepChildren}
        onDeleteAll={handleDeleteAll}
        childCount={actualChildIds.length}
      />

      <ReferenceSelector
        isOpen={showRefSelector}
        onClose={() => setShowRefSelector(false)}
        onSave={handleRefSelectorSave}
        excludeNodeId={id}
        canvasNodes={allNodes}
        maxReferences={99}
        visibleTabs={refSelectorVisibleTabs}
      />
    </div>
  )
})

GroupNode.displayName = 'GroupNode'
export { GroupNode }
